import { useQuery } from '@tanstack/react-query';

import type { GetProgressResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get order progress by ID
 * @param servicesProviderId - Required services provider ID
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 * @returns Order progress with categorized action items grouped by operation type
 */
export const getOrderProgress = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetProgressResponse> => {
	const response = await apiClient.get<GetProgressResponse>(
		ordersEndpoints.progress(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for fetching order progress by ID
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetOrderProgress = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to fetch order progress without an order ID.');
			}
			return getOrderProgress(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
